// Only used in Pricing module

import * as React from 'react'
import styled, { css } from 'styled-components'
import { ThemeName } from '../../../lib/constants'
import { typoHeading5, typoParagraphS } from '../../../lib/styles/Typography'
import { styleCheckListBlack, styleCheckListWhite } from '../../../lib/styles/Style'
import { Button } from '../Button'
import { Card } from '../Card'
import CustomLink from '../../CustomLink'
import { IconListItem } from '../IconList/types'
import { SbAsset } from '../../../types'

export interface TeaserCardProps {
  isItem?: boolean
  isProductItem?: boolean
  href?: string
  asset?: SbAsset
  children: React.ReactNode
  step?: string
  title: string
  disclaimer: string
  category?: string
  products?: string
  icons?: IconListItem[]
  infos?: string[]
  buttonLabel?: string
  justify?: 'left' | 'center'
  direction?: 'row' | 'column'
  theme?: ThemeName
  color?: string
  [key: string]: any
}

const CardInner = styled.div<{
  justify?: 'left' | 'center'
  direction?: 'row' | 'column'
  color?: string
}>`
  display: flex;
  flex-direction: ${({ direction }) => direction || 'column'};
  align-items: ${({ justify }) => (justify === 'left' ? 'flex-start' : 'center')};
  text-align: ${({ justify }) => justify};
  gap: ${({ direction }) => direction === 'column' ? '1rem' : '0'};
  padding: ${({ direction }) => direction === 'column' ? '1.25rem' : '0.75em'};
  justify-content: space-between;
  height: 100%;
  border-radius: 14px;
  background-color: ${({ color }) => color || '#FFF'};
  color: ${({ color }) => color === 'black' ? '#FFF' : '#000'};
`

const CardTitle = styled.h5<{ color?: string }>`
  ${typoHeading5}
  font-size: 24px;
  font-weight: 700;
  line-height: 150%;
  color: ${({ color }) => color === 'black' ? '#DAD9D8' : '#676765'};
`

const CardCategory = styled.p<{ color?: string }>`
  ${typoParagraphS}
  font-family: 'Source Sans Pro';
  font-size: 40px;
  font-weight: 900;
  line-height: 115%;
  color: ${({ color }) => color === 'black' ? '#FFF' : '#000'};
`

const CardContent = styled.div<{ color?: string }>`
  ${typoParagraphS}
  ${({ color }) => color === 'white' ? styleCheckListBlack : styleCheckListWhite}
  margin: 0;
`

const CardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  gap: 1rem;
`

const ButtonWrapper = styled.div`
  padding-top: 1.5rem;
  width: 100%;
`

const FlexContainer = styled.div<{ direction?: 'row' | 'column' }>`
  display: flex;
  align-items: ${({ direction }) => direction === 'column' ? 'start' : 'center'};
  flex-direction: ${({ direction }) => direction};
  gap: ${({ direction }) => direction === 'column' ? '1.5em' : '1em'};
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
`

const WatermarkBadge = styled.div`
  background-color: #000;
  color: #FFF;
  padding: 0.5em 0.75em;
  border-radius: 6px;
  font-size: 0.8em;
  font-weight: 600;
  font-family: 'Source Sans Pro';
  line-height: 14px;
`

const PriceDescription = styled.div`
  color: #676765;
  font-size: 14px;
  font-weight: 600;
  line-height: 110%;
  font-family: 'Source Sans Pro';
`

const CardText = styled.p<{ color?: string }>`
  line-height: 110%;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  font-weight: 600;
  color: ${({ color }) => color === 'black' ? '#DAD9D8' : '#676765'};
`

const DisclaimerText = styled.div<{ color?: string }>`
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  text-align: center;
  font-family: 'Source Sans Pro';
  color: ${({ color }) => color === 'black' ? '#DAD9D8' : '#676765'};
`

const FooterContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 1rem;
  height: 6em;
`

export const TeaserCardV2 = ({
  buttonLabel,
  children,
  justify = 'center',
  direction = 'column',
  title,
  category,
  disclaimer,
  watermarked,
  color,
  priceDescription,
  text,
  theme,
  price,
  ...rest
}: TeaserCardProps) => {
  const isLink = !!rest.href || !!rest.to

  return (
    <Card
      theme={theme}
      as={isLink ? CustomLink : undefined}
      noBorder={true}
      target="_blank"
      {...rest}
      href={rest.href + (title ? `?plan=${title}` : ``)}
    >
      <CardInner justify={justify} direction={direction} color={color}>
        <FlexContainer direction={direction}>
          <CardContentWrapper>
            <FlexContainer direction="column">
              <div>
                <TitleContainer>
                  <CardTitle color={color}>{title}</CardTitle>
                  {watermarked && <WatermarkBadge>Recommended</WatermarkBadge>}
                </TitleContainer>
                <TitleContainer>
                  <CardCategory color={color}>{price}</CardCategory>
                  {priceDescription && <PriceDescription>{priceDescription}</PriceDescription>}
                </TitleContainer>
              </div>
              <CardText color={color}>{text}</CardText>
            </FlexContainer>
            <CardContent color={color}>{children}</CardContent>
          </CardContentWrapper>
        </FlexContainer>

        <FooterContainer>
          {isLink && (
            <ButtonWrapper className="theme-reset">
              <Button
                as="span"
                $ignoreTheme={true}
                $size="small"
                $fullWidth={true}
                iconRight={buttonLabel ? undefined : 'arrow-right'}
                $variant={color === 'white' ? 'primary' : 'quinary'}
              >
                {buttonLabel}
              </Button>
            </ButtonWrapper>
          )}
          <DisclaimerText color={color}>{disclaimer}</DisclaimerText>
        </FooterContainer>
      </CardInner>
    </Card>
  )
}
